/**
 * Marketing content for the public landing page.
 * Grounded in real Enable LXP modules — not speculative features.
 */

export const HERO_FALLBACK = {
  title: 'Empower Learning. Liberate Potential. Drive Growth.',
  subtitle:
    'Enable LXP is Liberate Global’s enterprise learning platform — deliver SCORM courses, live training, assessments, and certificates, with EduGenie as your AI learning assistant.',
  ctaText: 'Sign In',
  ctaUrl: '/login',
};

export const TRUST_STATS = [
  { value: '30', label: 'Years Delivering Proven Impact' },
  { value: '10M', label: 'Learners Empowered' },
  { value: '26', label: 'Verticals Serviced' },
  { value: '600', label: 'Global Awards & Rankings' },
];

export const PLATFORM_STATS = [
  { value: '10k+', label: 'Enrolled Learners' },
  { value: '200+', label: 'Expert Instructors' },
  { value: '50+', label: 'Quality Courses' },
  { value: '4', label: 'Role-Ready Panels' },
];

export const AUDIENCES = [
  {
    title: 'Enterprise L&D',
    desc: 'Scale workforce learning across organizations with licenses, roles, groups, and audit-ready reports.',
    points: ['Multi-org tenancy', 'License pools', 'Custom role access'],
  },
  {
    title: 'Instructors & Trainers',
    desc: 'Run courses, VILT sessions, forums, and surveys from a dedicated instructor workspace.',
    points: ['Live & async VILT', 'Course cohorts', 'Learner progress'],
  },
  {
    title: 'Learners',
    desc: 'Browse, learn, earn certificates and badges — and get instant help from EduGenie.',
    points: ['SCORM resume', 'Assessments', 'Certificates & badges'],
  },
];

export const PRODUCT_PILLARS = [
  {
    key: 'courses',
    title: 'Courses & SCORM Delivery',
    description:
      'Build and deliver SCORM 1.2/2004 courses with bookmark resume, enrollments, and a public catalog learners can explore.',
  },
  {
    key: 'assessments',
    title: 'Assessments & Quizzes',
    description:
      'Create timed assessments with pass thresholds, assignments, and completion analytics that prove mastery.',
  },
  {
    key: 'vilt',
    title: 'Virtual-Led Training',
    description:
      'Schedule live and on-demand sessions with meeting links, documents, video, and calendar visibility.',
  },
  {
    key: 'certificates',
    title: 'Certificates & Skills',
    description:
      'Issue branded certificates, tag courses to a skills taxonomy, and let learners download credentials anytime.',
  },
  {
    key: 'gamification',
    title: 'Gamification & Leaderboards',
    description:
      'Reward logins, completions, assessments, and forums with points, badge tiers, and org-wide leaderboards.',
  },
  {
    key: 'enterprise',
    title: 'Enterprise Org Management',
    description:
      'Manage organizations, license pools, bulk users, groups, and per-role menus across Admin, Org, Instructor, and Learner panels.',
  },
  {
    key: 'analytics',
    title: 'Analytics & Reporting',
    description:
      'Export enrollments, completions, surveys, tickets, and transactions — the data L&D leaders need to show ROI.',
  },
  {
    key: 'edugenie',
    title: 'EduGenie AI Assistant',
    description:
      'An intelligent in-app assistant that answers questions about courses, assessments, certificates, VILT, badges, and support.',
  },
];

export const SOLUTIONS = [
  {
    title: 'Induction & Onboarding',
    desc: 'Get new hires productive faster with structured courses, assessments, and completion tracking.',
  },
  {
    title: 'Upskilling & Reskilling',
    desc: 'Close skill gaps with skills-tagged courses, quizzes, and badge-driven motivation.',
  },
  {
    title: 'Compliance Training',
    desc: 'Assign mandatory learning, capture certificates, and export audit-friendly completion reports.',
  },
  {
    title: 'Leadership Development',
    desc: 'Combine VILT, forums, and assessments to grow managers and high-potential talent.',
  },
  {
    title: 'Frontline Enablement',
    desc: 'Mobile-friendly learning with SCORM resume, live sessions, and always-on EduGenie help.',
  },
  {
    title: 'Customer & Partner Training',
    desc: 'Publish a course catalog, enroll external learners, and support paid access with vouchers.',
  },
];

export const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Enroll & assign',
    desc: 'Provision users with licenses, assign courses by org or group, or let learners self-enroll from the catalog.',
  },
  {
    step: '02',
    title: 'Learn & practice',
    desc: 'Complete SCORM modules, join VILT sessions, take assessments, and engage in forums — with EduGenie on hand.',
  },
  {
    step: '03',
    title: 'Prove & celebrate',
    desc: 'Earn certificates, unlock badges, climb the leaderboard, and surface progress in real-time reports.',
  },
  {
    step: '04',
    title: 'Measure & improve',
    desc: 'Use analytics, surveys, and support tickets to refine programs and demonstrate learning ROI.',
  },
];

export const HERO_HIGHLIGHTS = [
  'EduGenie AI learning assistant',
  'SCORM courses with resume',
  'Assessments & certificates',
  'VILT, badges & analytics',
];

export const ENTERPRISE_POINTS = [
  {
    title: 'Four role-ready experiences',
    desc: 'Super Admin, Org Admin, Instructor, and Learner panels — each with the right tools for the job.',
  },
  {
    title: 'Licenses & multi-org scale',
    desc: 'Allocate license pools, bulk-provision users, and control access with groups and custom roles.',
  },
  {
    title: 'Commerce-ready catalog',
    desc: 'Public course browse, vouchers, and Stripe/PayPal checkout when you need paid learning pathways.',
  },
  {
    title: 'Governance built in',
    desc: 'Support tickets, notifications, 2FA, surveys, and FAQ CMS keep operations secure and learner-friendly.',
  },
];

/** Re-export Liberate testimonials for landing defaults */
export { LIBERATE_TESTIMONIALS as DEFAULT_TESTIMONIALS } from './landingConstants';
